/**
 * MONSTER TOWN — "A DAY IN TOWN" PERSISTENCE. ARCHITECT-OWNED.
 *
 * Every save section the expansion adds, registered in ONE place so the whole
 * new surface can be reviewed against the migration in `save.ts` side by side.
 *
 * SECTION IDS ARE STABLE FOREVER: time · needs · errands · inventory ·
 * collection · gifts · visit. Claimed here; no crew may reuse one.
 *
 * WHY THESE ARE NOT SPREAD ACROSS THE CREWS
 * The existing sections (`placements`, `wallet`, `monster`, ...) each live with
 * the crew that owns the state, which is right. These seven are different: they
 * are CORE state that six crews all read, they are the entire content of a
 * schema bump, and the migration that seeds them lives in `save.ts`. Keeping the
 * defaults, the hydration and the migration within one screen of each other is
 * the only way the next person can check that they agree.
 *
 * EVERY `hydrate` HERE IS TOTAL. It cannot throw, it never trusts a field, and
 * the worst case for any of them is "this kid starts the morning with an empty
 * bag" — never a lost town.
 *
 * REGISTERED FROM `main.ts` BEFORE `save.load()`. A section registered late gets
 * defaults for ever.
 */

import { collection, type CollectionSaveState, type FoundRecord } from './collection'
import { errands, type ErrandsSaveState } from './errands'
import { gifts, type GiftEntry, type GiftsSaveState } from './gifts'
import { inventory, type InventorySaveState } from './inventory'
import { needs, type NeedsSaveState } from './schedule'
import { registerSaveSection, save } from './save'
import { clock, hourToT, MORNING_HOUR, type TimeSaveState } from './time'
import type { SaveSlot } from './types'

/** Where a kid has been, and the photos they brought home. Their own slot only. */
export interface VisitSaveState {
  /** Real-clock ms of the last visit, for "you have not been in a while". */
  lastVisitedAt: number
  /** Photo ids in the kid's own album. Data ids, never image bytes. */
  photos: string[]
}

let visitState: VisitSaveState = { lastVisitedAt: 0, photos: [] }

export function visitAlbum(): Readonly<VisitSaveState> {
  return visitState
}

export function noteVisit(photoId?: string): void {
  visitState = {
    lastVisitedAt: Date.now(),
    photos: photoId === undefined ? visitState.photos : [...visitState.photos, photoId].slice(-64),
  }
  save.request('visit:noted')
}

/** The slot that is NOT the one being played. Two kids, so this is total. */
export function otherSlot(slot: SaveSlot): SaveSlot {
  return slot === 'a' ? 'b' : 'a'
}

let installed = false

export function installDaySave(): void {
  if (installed) return
  installed = true

  // -- time ----------------------------------------------------------------
  registerSaveSection<TimeSaveState>(
    {
      id: 'time',
      version: 1,
      defaults: () => ({ day: 1, t: hourToT(MORNING_HOUR) }),
      serialize: () => clock.snapshot(),
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        const day = Math.max(1, Math.round(c.num(raw['day'], c.defaults.day)))
        let t = c.num(raw['t'], c.defaults.t)
        if (!Number.isFinite(t)) t = c.defaults.t
        t = ((t % 1) + 1) % 1
        return { day, t }
      },
    },
    (v) => clock.restore(v),
  )

  // -- needs (invitations only — see schedule.ts) ---------------------------
  registerSaveSection<NeedsSaveState>(
    {
      id: 'needs',
      version: 1,
      defaults: () => ({ acceptedOn: {} }),
      serialize: () => needs.snapshot(),
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        const src = c.isRecord(raw['acceptedOn']) ? raw['acceptedOn'] : {}
        const acceptedOn: Record<string, number> = {}
        for (const k of ['sleepy', 'hungry']) {
          const n = Math.round(c.num(src[k], 0))
          if (n > 0) acceptedOn[k] = n
        }
        return { acceptedOn }
      },
    },
    (v) => needs.restore(v),
  )

  // -- errands -------------------------------------------------------------
  registerSaveSection<ErrandsSaveState>(
    {
      id: 'errands',
      version: 1,
      defaults: () => ({ runs: [], done: [] }),
      serialize: () => errands.snapshot(),
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        return {
          runs: c.arr(raw['runs'], (e) => {
            if (!c.isRecord(e)) return null
            const id = c.str(e['id'], '')
            if (id === '') return null
            return {
              id,
              step: Math.max(0, Math.round(c.num(e['step'], 0))),
              have: Math.max(0, Math.round(c.num(e['have'], 0))),
              hidden: c.bool(e['hidden'], false),
              grantedDay: Math.max(1, Math.round(c.num(e['grantedDay'], 1))),
            }
          }),
          done: c.arr(raw['done'], (e) => {
            if (!c.isRecord(e)) return null
            const id = c.str(e['id'], '')
            if (id === '') return null
            return { id, day: Math.max(0, Math.round(c.num(e['day'], 0))) }
          }),
        }
      },
    },
    (v) => errands.restore(v),
  )

  // -- inventory -----------------------------------------------------------
  registerSaveSection<InventorySaveState>(
    {
      id: 'inventory',
      version: 1,
      defaults: () => ({ items: {} }),
      serialize: () => inventory.snapshot(),
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        const src = c.isRecord(raw['items']) ? raw['items'] : {}
        const items: Record<string, number> = {}
        for (const [k, v] of Object.entries(src)) {
          if (k === '') continue
          const n = Math.round(c.num(v, 0))
          // A single junk row is dropped; the rest of the bag survives.
          if (n > 0) items[k] = n
        }
        return { items }
      },
    },
    (v) => inventory.restore(v),
  )

  // -- collection book -----------------------------------------------------
  registerSaveSection<CollectionSaveState>(
    {
      id: 'collection',
      version: 1,
      defaults: () => ({ found: {} }),
      serialize: () => collection.snapshot(),
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        const src = c.isRecord(raw['found']) ? raw['found'] : {}
        const found: Record<string, FoundRecord> = {}
        for (const [k, v] of Object.entries(src)) {
          if (k === '' || !c.isRecord(v)) continue
          const count = Math.round(c.num(v['count'], 0))
          if (count <= 0) continue
          found[k] = { count, firstDay: Math.max(1, Math.round(c.num(v['firstDay'], 1))) }
        }
        return { found }
      },
    },
    (v) => collection.restore(v),
  )

  // -- gifts (see gifts.ts: writes only ever land in the OWNER's slot) ------
  registerSaveSection<GiftsSaveState>(
    {
      id: 'gifts',
      version: 1,
      defaults: () => ({ outbox: [], inbox: [], claimedIds: [] }),
      serialize: () => gifts.snapshot(),
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        const entry = (e: unknown): GiftEntry | null => {
          if (!c.isRecord(e)) return null
          const id = c.str(e['id'], '')
          const itemId = c.str(e['itemId'], '')
          const count = Math.round(c.num(e['count'], 0))
          const from = c.str(e['fromSlot'], '')
          const to = c.str(e['toSlot'], '')
          if (id === '' || itemId === '' || count <= 0) return null
          if ((from !== 'a' && from !== 'b') || (to !== 'a' && to !== 'b')) return null
          return {
            id,
            fromSlot: from,
            toSlot: to,
            itemId,
            count,
            day: Math.max(1, Math.round(c.num(e['day'], 1))),
          }
        }
        return {
          outbox: c.arr(raw['outbox'], entry),
          inbox: c.arr(raw['inbox'], entry),
          claimedIds: c.arr<string>(raw['claimedIds'], (e) =>
            typeof e === 'string' && e !== '' ? e : null,
          ),
        }
      },
    },
    (v) => gifts.restore(v),
  )

  // -- visit metadata ------------------------------------------------------
  registerSaveSection<VisitSaveState>(
    {
      id: 'visit',
      version: 1,
      defaults: () => ({ lastVisitedAt: 0, photos: [] }),
      serialize: () => visitState,
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        return {
          lastVisitedAt: Math.max(0, c.num(raw['lastVisitedAt'], 0)),
          photos: c.arr<string>(raw['photos'], (e) =>
            typeof e === 'string' && e !== '' ? e : null,
          ).slice(-64),
        }
      },
    },
    (v) => {
      visitState = { lastVisitedAt: v.lastVisitedAt, photos: [...v.photos] }
    },
  )
}
